import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Spinner } from '@iq/react-components';
import config from '../../../config';

import {
  requestSources,
  createSource,
  updateSource,
  deleteSource,
  getSources,
  getSourcesLoaded,
  getSourceTypes,
} from '../../../bundles/sources';
import { getActiveSite } from '../../../bundles/sites';
import Heading from '../../Heading';
import ConfirmationDialog from '../../ConfirmationDialog';
import SourceListItem from './components/SourceListItem';
import CreateSourceModal from './components/CreateSourceModal';
import { INTERNAL_DATA_SOURCE } from '../../../constants';

const SECRET_KEYS = new Set(['secret', 'clientSecret', 'client_secret', 'password']);

const transformErrors = (errors) =>
  errors.map((error) => {
    const customError = { ...error };
    if (customError.name === 'format' && customError.property === '.name') {
      customError.message = String.raw`Source name may not contain the characters [ ] * ? : / \ or the character ' (apostrophe) in the first or last position.`;
    }
    return customError;
  });

const SourceView = ({ wizardType }) => {
  const dispatch = useDispatch();
  const site = useSelector(getActiveSite);
  const sourcesLoaded = useSelector(getSourcesLoaded);
  const sources = useSelector(getSources);
  const sourceTypes = useSelector(getSourceTypes);

  const [showNoSourceDeleteMessage, setShowNoSourceDeleteMessage] = useState(false);
  const [displayCreateSourceForm, setDisplayCreateSourceForm] = useState(false);

  useEffect(() => {
    if (site.id && sourcesLoaded !== site.id) {
      dispatch(requestSources({ siteId: site.id, withVariables: false }));
    }
  }, [site, sourcesLoaded, dispatch]);

  const sortedSources = sources.toSorted(({ createdAt: a }, { createdAt: b }) => a - b);

  const formatSchema = (sourceType, ignoreProperties = [], isUpdate = false) => {
    if (sourceType?.name === INTERNAL_DATA_SOURCE && !config.onPremise) {
      return {}; // no schema/updates for internal data source on cloud
    }

    // Handle other source types
    const options =
      Object.entries(sourceType?.schemas?.source?.properties || {}).length > 0
        ? structuredClone(sourceType.schemas.source)
        : {};
    const riskThreshold = { ...options?.properties?.riskThreshold };

    // For DEC sources on create, clientSecret is required
    if (!isUpdate && sourceType?.name === 'dec' && options?.properties?.clientSecret) {
      if (!options.required) options.required = [];
      if (!options.required.includes('clientSecret')) {
        options.required = [...options.required, 'clientSecret'];
      }
    }

    const schema = {
      type: 'object',
      required: ['name', 'type'],
      properties: {
        type: {
          type: 'string',
          title: 'Source Type',
          enum: (sourceTypes || [])
            .map(({ name }) => name)
            .filter((name) => name !== INTERNAL_DATA_SOURCE),
        },
        name: {
          type: 'string',
          title: 'Name',
          format: 'sourceName',
          maxLength: 30,
        },
        description: {
          type: 'string',
          title: 'Description',
          default: '',
        },
        options:
          isUpdate && options.required
            ? {
                ...options,
                required: options.required.filter((field) => !SECRET_KEYS.has(field)),
              }
            : options,
      },
    };

    if (schema.properties?.options?.properties?.riskStatusEnabled) {
      // Delete this schema prop coming from IB. So that we can add dependencies here.
      delete schema.properties.options.properties.riskThreshold;
      schema.properties.options.dependencies = {
        riskStatusEnabled: {
          oneOf: [
            {
              properties: {
                riskStatusEnabled: { enum: [false] },
              },
            },
            {
              properties: {
                riskStatusEnabled: { enum: [true] },
                riskThreshold,
              },
            },
          ],
        },
      };
    }

    if (schema.properties?.options?.properties?.importedFileName) {
      delete schema.properties.options.properties.importedFileName;
    }

    // Remove elasticsearch connection fields (auto-populated from environment)
    if (sourceType?.name === 'elasticsearch' && schema.properties?.options?.properties) {
      for (const field of ['host', 'port', 'protocol', 'user', 'password']) {
        delete schema.properties.options.properties[field];
      }
      if (schema.properties.options.required) {
        schema.properties.options.required = schema.properties.options.required.filter(
          (field) => !['host', 'port', 'protocol', 'user', 'password'].includes(field)
        );
      }
    }

    if (isUpdate && sourceType?.name === 'dec' && schema.properties?.options?.properties?.tenant) {
      schema.properties.options.properties.tenant = {
        ...schema.properties.options.properties.tenant,
        disabled: true,
      };
    }

    for (const property of ignoreProperties) {
      delete schema.properties[property];
    }

    if (sourceType?.name === INTERNAL_DATA_SOURCE) {
      delete schema.properties.name;
      delete schema.properties.description;
    }

    // Mark secret fields so TextWidget renders them as password inputs with eye toggle
    if (schema.properties?.options?.properties) {
      for (const [key, property] of Object.entries(schema.properties.options.properties)) {
        if (SECRET_KEYS.has(key)) {
          schema.properties.options.properties[key] = { ...property, inputType: 'password' };
        }
      }
    }

    return schema;
  };

  const customFormats = {
    // eslint-disable-next-line no-useless-escape
    sourceName: /^[^\'\[\]\*\?\:\/\\]*[^\'\[\]\*\?\:\/\\]$/,
  };

  const customValidate = useCallback(
    (formData, errors) => {
      for (const source of sources) {
        if (
          source.id !== formData.id &&
          source.name.toLowerCase() === formData.name?.toLowerCase()
        ) {
          errors.name.addError('Source name should be unique.');
        }
        if (source.id === formData.id && formData.options?.riskThreshold) {
          const { shutdownWarning, shutdownEmergency, downtimeWarning, downtimeEmergency } =
            formData.options.riskThreshold;
          const thresholdErrors = errors.options.riskThreshold;
          if (shutdownWarning <= 0 || shutdownWarning > 100) {
            thresholdErrors.shutdownWarning.addError('The range should be between 0 and 100.');
          }
          if (shutdownEmergency <= 0 || shutdownEmergency > 100) {
            thresholdErrors.shutdownEmergency.addError('The range should be between 0 and 100.');
          }
          if (downtimeWarning <= 0) {
            thresholdErrors.downtimeWarning.addError('The number must exceed 0.');
          }
          if (downtimeEmergency <= 0) {
            thresholdErrors.downtimeEmergency.addError('The number must exceed 0.');
          }
        }
      }

      return errors;
    },
    [sources]
  );

  const createSchemas = useMemo(() => {
    const schemas = { default: formatSchema() };
    for (const sourceType of sourceTypes) {
      if (sourceType.name !== INTERNAL_DATA_SOURCE || config.onPremise) {
        schemas[sourceType.name] = formatSchema(sourceType);
      }
    }
    return schemas;
  }, [sourceTypes]);

  const updateSchemas = useMemo(() => {
    const schemas = { default: formatSchema() };
    for (const sourceType of sourceTypes) {
      schemas[sourceType.name] = formatSchema(sourceType, ['type'], true);
    }
    return schemas;
  }, [sourceTypes]);

  const updateUiSchemas = useMemo(() => {
    const schemas = {};
    for (const sourceType of sourceTypes) {
      if (sourceType.name === 'dec') {
        schemas[sourceType.name] = {
          options: {
            tenant: {
              'ui:disabled': true,
              'ui:help':
                'Tenant ID cannot be changed after creation. To use a different tenant, please create a new source.',
            },
          },
        };
      }
    }
    return schemas;
  }, [sourceTypes]);

  const onDelete = useCallback(
    (sourceId) => {
      const source = sources.find((s) => s.id === sourceId);
      if (source?.type === INTERNAL_DATA_SOURCE) {
        setShowNoSourceDeleteMessage(true);
      } else {
        dispatch(deleteSource(site.id, sourceId));
      }
    },
    [sources, dispatch, site.id]
  );

  const onUpdate = useCallback(
    (sourceId, data, onSuccess) => dispatch(updateSource(site.id, sourceId, data, { onSuccess })),
    [dispatch, site.id]
  );

  const onCreate = useCallback(
    (formData) => {
      const sourceData = { org: site.org, site: site.id, ...formData };

      // Inject predefined values for identiq source
      if (formData.type === 'identiq') {
        sourceData.name = 'Identiq';
        sourceData.description = 'Internal source';
      }

      dispatch(
        createSource(sourceData, {
          onSuccess: () => setDisplayCreateSourceForm(false),
        })
      );
      // Modal stays open until onSuccess fires — keeps form editable on failure
    },
    [dispatch, site.org, site.id]
  );

  const onClose = useCallback(() => {
    setDisplayCreateSourceForm(false);
  }, [setDisplayCreateSourceForm]);

  const wizClass = wizardType ? `--${wizardType}` : '';

  return (
    <>
      {sourcesLoaded ? (
        <>
          <Heading
            contentLeft={<div className="title">Data Sources</div>}
            contentRight={
              config.onPremise ? null : (
                <Button
                  className={`button-create${wizardType ? wizClass : ''}`}
                  onClick={() => setDisplayCreateSourceForm(true)}
                >
                  Add Source
                </Button>
              )
            }
          />

          {sortedSources.length > 0 ? (
            sortedSources.map((source, sourceIndex) => (
              <SourceListItem
                site={site}
                title={source.name}
                columnCount={sortedSources.length}
                sourceIndex={sourceIndex}
                key={source.id}
                source={source}
                schema={updateSchemas[source.type] || updateSchemas.default}
                uiSchema={updateUiSchemas[source.type]}
                onDelete={onDelete}
                onSubmit={onUpdate}
                transformErrors={transformErrors}
                customFormats={customFormats}
                customValidate={customValidate}
                isEditable={source.type !== INTERNAL_DATA_SOURCE || config.onPremise}
              />
            ))
          ) : (
            <div>No sources yet, create one to get started</div>
          )}
        </>
      ) : (
        <div className="loading-container">
          <Spinner
            size="s"
            className="spinner"
          />
        </div>
      )}

      {displayCreateSourceForm && sourceTypes.length > 0 && (
        <CreateSourceModal
          schemas={createSchemas}
          onSubmit={onCreate}
          onCloseModal={onClose}
          transformErrors={transformErrors}
          customFormats={customFormats}
          customValidate={customValidate}
        />
      )}

      {showNoSourceDeleteMessage && (
        <ConfirmationDialog
          onCancel={() => setShowNoSourceDeleteMessage(false)}
          onConfirm={(event) => {
            event.preventDefault();
            setShowNoSourceDeleteMessage(false);
          }}
          confirmType="info"
          title="Remove source"
          body={
            <div className="remove-internal-source">
              <div>Internal sources can not be removed.</div>
            </div>
          }
          confirmText="OK"
        />
      )}
    </>
  );
};

export default SourceView;
